<template>
	<v-slider
		v-model="sliderValue"
		:disabled="controllerType === ControllerTypes.Rodecaster"
		class="large-slider"
		direction="vertical"
		show-ticks="always"
		tick-size="1"
		thumb-size="60"
		step="1"
		min="0"
		max="100"
		:color="sliderPressed ? 'blue-lighten-1' : 'black'"
		:elevation="0"
		@update:model-value="updateFader"
	/>
</template>

<script>
/* Libraries */
import emitter from "@/emitter";

/* Enums */
import ControllerTypes from "@/enums/ControllerTypes";

export default {
	name: "Fader",

	props: ["channelData", "controllerType"],

	data() {
		return {
			sliderValue: 80,
			sliderPressed: false
		};
	},

	computed: {
		ControllerTypes() {
			return ControllerTypes;
		},

		useLogarithmicScale() {
			return this.$store.getters.getStudioSettings()?.preferences?.midi?.use_logarithmic_scale;
		}
	},

	mounted() {
		this.$worker.setTimeout(() => {
			if (this.channelData.mode === "master" || this.channelData.mode === "track_control") {
				let faderValues = JSON.parse(localStorage.getItem("faderValues")) ?? {};
				this.sliderValue = faderValues[this.channelData.assignment] ?? this.sliderValue;
			} else if (this.channelData.mode === "mic_control" || this.channelData.mode === "hotkey_control") {
				let faderValues = JSON.parse(localStorage.getItem("faderValues")) ?? {};
				this.sliderValue = faderValues[this.channelData.id] ?? this.sliderValue;
			}
			this.updateFader();
		}, 1000);

		if (this.channelData.assignment && this.controllerType !== ControllerTypes.Rodecaster) {
			this.initializeFader();
		}

		emitter.on("fader:moved", this.handleFaderMoved);
		emitter.on("fader:pressed", this.handleFaderPressed);
		emitter.on("updateControllerFader", this.handleUpdateControllerFader);

		if (this.controllerType !== ControllerTypes.Rodecaster) {
			emitter.on("reinitializeMIDI", this.initializeFader);
		}
	},

	beforeUnmount() {
		emitter.off("reinitializeMIDI", this.initializeFader);
		emitter.off("fader:moved", this.handleFaderMoved);
		emitter.off("fader:pressed", this.handleFaderPressed);
		emitter.off("updateControllerFader", this.handleUpdateControllerFader);
	},

	methods: {
		initializeFader() {
			emitter.emit("onSetFader", {
				channel: this.channelData.channelNumber,
				value: this.scaleValue(this.sliderValue),
				mode: this.channelData.mode
			});
		},

		updateFader(ev) {
			this.setLocalStorageValue();

			if (this.channelData.mode === "master") {
				// if ev is object than the event has come from the controller
				if (
					typeof ev !== "object" &&
					this.channelData.assignment &&
					this.controllerType !== ControllerTypes.Rodecaster
				) {
					emitter.emit("onSetFader", {
						channel: this.channelData.channelNumber,
						value: this.scaleValue(this.sliderValue),
						mode: this.channelData.mode
					});
				}
				this.channelData.mainMidiBC.postMessage({
					type: "setGainMaster",
					gain: this.useLogarithmicScale
						? this.toRatio(this.scaleValue(this.sliderValue))
						: this.sliderValue / 100
				});
			} else if (this.channelData.mode === "track_control") {
				// if ev is object than the event has come from the controller
				if (
					typeof ev !== "object" &&
					this.channelData.assignment &&
					this.controllerType !== ControllerTypes.Rodecaster
				) {
					emitter.emit("onSetFader", {
						channel: this.channelData.channelNumber,
						value: this.scaleValue(this.sliderValue),
						mode: this.channelData.mode
					});
				}
				this.channelData.mainMidiBC.postMessage({
					type: "setGainChannel",
					gain: this.useLogarithmicScale
						? this.toRatio(this.scaleValue(this.sliderValue))
						: this.sliderValue / 100,
					channel: this.channelData.channelNumber.toString()
				});
			} else if (this.channelData.mode === "hotkey_control") {
				// if ev is object than the event has come from the controller
				if (
					typeof ev !== "object" &&
					this.channelData.assignment &&
					this.controllerType !== ControllerTypes.Rodecaster
				) {
					emitter.emit("onSetFader", {
						channel: this.channelData.channelNumber,
						value: this.scaleValue(this.sliderValue),
						mode: this.channelData.mode
					});
				}
				this.channelData.hotkeysMidiBC.postMessage({
					type: "setHotkeyAudioGain",
					gain: this.toRatio(this.scaleValue(this.sliderValue)),
					bank: this.channelData.channelNumber
				});
			} else if (this.channelData.mode === "mic_control") {
				// if ev is object then the event has come from the controller
				if (
					typeof ev !== "object" &&
					this.channelData.assignment &&
					this.controllerType !== ControllerTypes.Rodecaster
				) {
					emitter.emit("onSetFader", {
						channel: this.channelData.channelNumber,
						value: this.scaleValue(this.sliderValue),
						mode: this.channelData.mode
					});
				}
				this.channelData.mainMidiBC.postMessage({
					type: "setGainMic",
					gain: this.useLogarithmicScale
						? this.toRatio(this.scaleValue(this.sliderValue))
						: this.sliderValue / 100,
					user: this.channelData.peerID
				});
			}
		},

		scaleValue(value) {
			return parseInt((value / 100) * 127);
		},

		scaleDownValue(value) {
			return parseInt((value / 127) * 100);
		},

		dBDeltaToRatio(dBDelta) {
			return Math.pow(10, dBDelta / 20.0);
		},

		toRatio(value) {
			const A1 = 28 / 71;
			const A2 = 1;
			const A3 = 22 / 9;
			const B1 = -28 - 812 / 71;
			const B2 = -57;
			const B3 = -70;

			if (value >= 29) {
				return this.dBDeltaToRatio(A1 * value + B1);
			} else if (value > 9) {
				return this.dBDeltaToRatio(A2 * value + B2);
			} else if (value > 0) {
				return this.dBDeltaToRatio(A3 * value + B3);
			} else {
				return 0;
			}
		},

		setLocalStorageValue() {
			if (this.channelData.mode === "master" || this.channelData.mode === "track_control") {
				let faderValues = JSON.parse(localStorage.getItem("faderValues")) ?? {};
				faderValues[this.channelData.assignment] = this.sliderValue;

				localStorage.setItem("faderValues", JSON.stringify(faderValues));
			} else if (this.channelData.mode === "mic_control" || this.channelData.mode === "hotkey_control") {
				let faderValues = JSON.parse(localStorage.getItem("faderValues")) ?? {};
				faderValues[this.channelData.id] = this.sliderValue;

				localStorage.setItem("faderValues", JSON.stringify(faderValues));
			}
		},

		handleFaderMoved(ev) {
			if (this.channelData.mode === ev.mode && this.channelData.channelNumber === ev.channel) {
				this.sliderValue = this.scaleDownValue(ev.value);
				this.updateFader(ev);
			}
		},

		handleFaderPressed(ev) {
			if (this.channelData.mode === ev.mode && this.channelData.channelNumber === ev.channel) {
				this.sliderPressed = ev.pressed === 127;
			}
		},

		handleUpdateControllerFader(ev) {
			if (this.channelData.assignment === ev.newlyAssignedChannel) {
				this.updateFader();
			}
		}
	}
};
</script>

<style scoped lang="scss">
:deep(.v-slider-track__ticks) {
	display: flex;
	justify-content: center;

	.v-slider-track__tick {
		margin-left: 1px;
		width: 0;

		&:first-child,
		&:nth-child(10n) {
			background-color: rgba(0, 0, 0, 0.5);
			width: 60px;
		}
	}
}

:deep(.v-slider-thumb) {
	width: 40px;
	height: 60px;
	background: linear-gradient(145deg, #e0e0e0, #808080);
	border-radius: 4px;
	position: absolute;
	left: 50%;
	transform: translateX(-50%);
	top: 50%;
	box-shadow:
		inset 2px 2px 4px rgba(255, 255, 255, 0.5),
		inset -2px -2px 4px rgba(0, 0, 0, 0.4),
		0 3px 6px rgba(0, 0, 0, 0.7),
		0 -2px 2px rgba(255, 255, 255, 0.2);
	border: 1px solid #666;

	&::before {
		content: "";
		position: absolute;
		width: 100%;
		height: 50%;
		background: repeating-linear-gradient(
			to bottom,
			rgba(0, 0, 0, 1),
			rgba(0, 0, 0, 1) 2px,
			rgba(0, 0, 0, 0.4) 6px,
			rgba(0, 0, 0, 0.4) 7px,
			rgba(255, 255, 255, 1) 1px
		);
		border-radius: 2px 2px 0 0;
		border-bottom: 1px solid #fff;
	}

	&::after {
		content: "";
		position: absolute;
		top: 50%;
		width: 100%;
		height: 50%;
		background: repeating-linear-gradient(
			to top,
			rgba(0, 0, 0, 0.4),
			rgba(0, 0, 0, 0.4) 2px,
			rgba(0, 0, 1, 1) 6px,
			rgba(0, 0, 0, 1) 7px
		);
		border-radius: 0 0 2px 2px;
		border-top: 2px solid #fff;
	}

	.v-slider-thumb__ripple,
	.v-slider-thumb__surface {
		display: none;
	}
}
</style>
